import type { Ref } from 'vue';
import { Components } from '@/api/generated/api';
import { useSchoolClassAccess } from '@/composables/schoolClassAccess/useSchoolClassAccess';
import { useSchoolLevelsBySchoolType } from '@/composables/useSchoolLevelsBySchoolType';
import { useSchoolYears } from '@/composables/useSchoolYears';
import { IBumpUpClassConfig, IUserWithPermissions } from '@/interfaces';
import { immutableSort } from 'common/utils/array.utils';
import { useSchoolSubject } from './schoolSubject/useSchoolSubject';
import { matches } from 'common/utils/string.utils';

export const useBumpUpClass = (allStudentsAdmin: Ref<Components.Schemas.StudentDto[]>) => {
  const { getAvailableSchoolSubjectsForSchoolLevel, getSchoolSubjectsForClass } = useSchoolSubject();
  const { allAccessRights } = useSchoolClassAccess();
  const { allSchoolYearsTenant } = useSchoolYears();
  const { hasNextSchoolLevel } = useSchoolLevelsBySchoolType();

  const getNextSchoolYearId = (schoolClass: Components.Schemas.SchoolClassDto): number => {
    const sortedSchoolYears = immutableSort(
      allSchoolYearsTenant.value,
      (a, b) => new Date(a.schoolYearStart).getTime() - new Date(b.schoolYearStart).getTime(),
    );
    const currentSchoolYearId = schoolClass.schoolYearId;
    const currentSchoolYearIndex = sortedSchoolYears.findIndex((year) => year.id === currentSchoolYearId);
    return sortedSchoolYears[currentSchoolYearIndex + 1]?.id ?? currentSchoolYearId;
  };

  const getNextSchoolLevel = (schoolClass: Components.Schemas.SchoolClassDto): number => {
    const nextSchoolLevel = schoolClass.schoolLevel + 1;
    return hasNextSchoolLevel(schoolClass.schoolLevel) ? nextSchoolLevel : schoolClass.schoolLevel;
  };

  const getNextSchoolSubjects = async (
    schoolClass: Components.Schemas.SchoolClassDto,
    nextSchoolLevel: number,
  ): Promise<Components.Schemas.SchoolSubjectDto[]> => {
    const currentSchoolSubjects = await getSchoolSubjectsForClass(schoolClass.id);
    const availableSchoolSubjects = await getAvailableSchoolSubjectsForSchoolLevel(nextSchoolLevel);

    const nextLevelSubjects = availableSchoolSubjects.filter((s) => s.schoolLevel === nextSchoolLevel);
    const nextSubjects: Components.Schemas.SchoolSubjectDto[] = [];
    const seenSubjectIds = new Set<number>();

    for (const subject of currentSchoolSubjects) {
      const nextSubject = nextLevelSubjects.find((s) => matches(s.name, subject.name));
      if (nextSubject && !seenSubjectIds.has(nextSubject.id)) {
        nextSubjects.push(nextSubject);
        seenSubjectIds.add(nextSubject.id);
      }
    }

    return nextSubjects;
  };

  const getNextTeam = (
    schoolClass: Components.Schemas.SchoolClassDto,
    nextSchoolSubjects: Components.Schemas.SchoolSubjectDto[],
  ): IUserWithPermissions[] => {
    const currentTeam = allAccessRights.value.filter((ar) => ar.schoolClass.id === schoolClass.id);
    const nextTeam: IUserWithPermissions[] = [];

    for (const teamMember of currentTeam) {
      const subjectNames = teamMember.permissions.subjects?.map((s) => s.name) ?? [];
      const schoolSubjects = nextSchoolSubjects.filter((s) => subjectNames.some((name) => matches(s.name, name)));

      const nextTeamMember = {
        ...teamMember,
        permissions: {
          ...teamMember.permissions,
          subjects: schoolSubjects,
        },
        schoolSubjects,
      };
      nextTeam.push(nextTeamMember);
    }

    return nextTeam;
  };

  const getExistingStudents = (schoolClass: Components.Schemas.SchoolClassDto): Components.Schemas.StudentDto[] => {
    return allStudentsAdmin.value.filter((s) => s.schoolClasses.some((sc) => sc.id === schoolClass.id));
  };

  const getBumpUpClassConfig = async (schoolClass: Components.Schemas.SchoolClassDto): Promise<IBumpUpClassConfig> => {
    const nextSchoolLevel = getNextSchoolLevel(schoolClass);
    const nextSchoolSubjects = await getNextSchoolSubjects(schoolClass, nextSchoolLevel);
    const nextTeam = getNextTeam(schoolClass, nextSchoolSubjects);
    const existingStudents = getExistingStudents(schoolClass);

    const generalData: Components.Schemas.CreateSchoolClassDto = {
      isActive: true,
      name: schoolClass.name,
      nickname: schoolClass.nickname ?? undefined,
      schoolLevel: nextSchoolLevel,
      schoolYearId: getNextSchoolYearId(schoolClass),
    };

    return {
      generalData,
      schoolSubjects: nextSchoolSubjects,
      team: nextTeam,
      existingStudents,
    };
  };

  return {
    getBumpUpClassConfig,
  };
};
